import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  Firestore
} from 'firebase/firestore';
import { getAuth, Auth } from 'firebase/auth';
import { getDatabase, Database } from 'firebase/database';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { REACT_APP_FIREBASE_CONFIG } from '../global/environment';

// Each service is created on first use and memoized, rather than at module import
// time. This shortens cold start and avoids opening connections (Firestore, Auth,
// Realtime Database, Storage) that a given page may never need.
let appInstance: FirebaseApp | undefined;
let firestoreInstance: Firestore | undefined;
let authInstance: Auth | undefined;
let realtimeDbInstance: Database | undefined;
let storageInstance: FirebaseStorage | undefined;

/**
 * Lazily initializes (and memoizes) the Firebase app from REACT_APP_FIREBASE_CONFIG.
 * @returns {FirebaseApp} The shared Firebase app instance.
 */
export const getFirebaseApp = (): FirebaseApp => {
  if (!appInstance) {
    const firebaseConfig = JSON.parse(REACT_APP_FIREBASE_CONFIG as string);
    appInstance = initializeApp(firebaseConfig);
  }
  return appInstance;
};

/**
 * Lazily initializes (and memoizes) Firestore with offline persistence so reads are
 * served from the local IndexedDB cache when data hasn't changed, significantly
 * reducing reads from Firestore servers. The multi-tab manager is used because users
 * may have multiple tabs open. If the browser doesn't support IndexedDB persistence,
 * Firestore automatically falls back to an in-memory cache.
 * @returns {Firestore} The shared Firestore instance.
 */
export const getFirestoreDb = (): Firestore => {
  if (!firestoreInstance) {
    firestoreInstance = initializeFirestore(getFirebaseApp(), {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    });
  }
  return firestoreInstance;
};

/**
 * Lazily initializes (and memoizes) Firebase Auth.
 * @returns {Auth} The shared Auth instance.
 */
export const getFirebaseAuth = (): Auth => {
  if (!authInstance) {
    authInstance = getAuth(getFirebaseApp());
  }
  return authInstance;
};

/**
 * Lazily initializes (and memoizes) the Firebase Realtime Database.
 * @returns {Database} The shared Realtime Database instance.
 */
export const getRealtimeDb = (): Database => {
  if (!realtimeDbInstance) {
    realtimeDbInstance = getDatabase(getFirebaseApp());
  }
  return realtimeDbInstance;
};

/**
 * Lazily initializes (and memoizes) Firebase Storage.
 * @returns {FirebaseStorage} The shared Storage instance.
 */
export const getFirebaseStorage = (): FirebaseStorage => {
  if (!storageInstance) {
    storageInstance = getStorage(getFirebaseApp());
  }
  return storageInstance;
};
